import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AccessControlDoorType,
  KeyRequestCredentialType,
  SiteSystemType,
} from "api/ApiClient";
import { apiClientFactory, getExternalTenantId } from "helpers";
import { useSelector } from "react-redux";
import { ApplicationState } from "store";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import CloudManagedWarningDialog from "./CloudManagedWarningDialog";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  groupId: string;
  groupSiteId?: string;
  cloudManagedSystemType?: SiteSystemType.Brivo | SiteSystemType.Openpath;
  assignedLockIds: string[];
};

const ALL_SITES_VALUE = "__all_sites__";

export default function AddLockToGroupModal({
  open,
  onClose,
  onAdded,
  groupId,
  groupSiteId,
  cloudManagedSystemType,
  assignedLockIds,
}: Props) {
  const me = useSelector((state: ApplicationState) => state.me);
  const [search, setSearch] = React.useState("");
  const [selectedSiteId, setSelectedSiteId] = React.useState("");
  const [selectedLockIds, setSelectedLockIds] = React.useState<string[]>([]);
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showCloudWarning, setShowCloudWarning] = React.useState(false);

  const tenantExternalId = getExternalTenantId(me.user);

  const { data: allLocks, isLoading } = useQuery({
    queryKey: ["acg", "locks", "available", groupId, tenantExternalId],
    queryFn: () => apiClientFactory().getLocks(0, 2000, undefined, tenantExternalId),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedSiteId(groupSiteId ?? "");
      setSelectedLockIds([]);
      setAdding(false);
      setError(null);
      setShowCloudWarning(false);
    }
  }, [groupSiteId, open]);

  const availableLocks = React.useMemo(() => {
    const assigned = new Set(assignedLockIds);

    return (allLocks ?? []).filter(
      (lock) => lock.id && !assigned.has(lock.id) && lock.externalId,
    );
  }, [allLocks, assignedLockIds]);

  const availableSites = React.useMemo(() => {
    const map = new Map<string, string>();

    for (const lock of availableLocks) {
      if (!lock.siteId) {
        continue;
      }
      map.set(lock.siteId, lock.site?.name ?? lock.siteId);
    }

    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [availableLocks]);

  const filteredLocks = React.useMemo(() => {
    const query = search.trim().toLowerCase();

    return availableLocks
      .filter((lock) => {
        if (groupSiteId && lock.siteId !== groupSiteId) {
          return false;
        }

        if (selectedSiteId && lock.siteId !== selectedSiteId) {
          return false;
        }

        if (!query) {
          return true;
        }

        const haystack = [
          lock.name,
          lock.systemLockId,
          lock.doorType,
          lock.site?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      })
      .sort((a, b) => {
        const siteCompare = (a.site?.name ?? "").localeCompare(b.site?.name ?? "");
        if (siteCompare !== 0) {
          return siteCompare;
        }

        return (a.name ?? "").localeCompare(b.name ?? "");
      });
  }, [availableLocks, groupSiteId, search, selectedSiteId]);

  const selectedSet = React.useMemo(() => new Set(selectedLockIds), [selectedLockIds]);
  const visibleIds = React.useMemo(
    () => filteredLocks.map((lock) => lock.id as string),
    [filteredLocks],
  );

  const selectedVisibleCount = visibleIds.filter((id) => selectedSet.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const toggleRowSelection = (lockId: string) => {
    setSelectedLockIds((prev) =>
      prev.includes(lockId)
        ? prev.filter((id) => id !== lockId)
        : [...prev, lockId],
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleSet = new Set(visibleIds);
      setSelectedLockIds((prev) => prev.filter((id) => !visibleSet.has(id)));
      return;
    }

    setSelectedLockIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        next.add(id);
      }
      return [...next];
    });
  };

  const runAdd = async () => {
    setAdding(true);
    setError(null);

    try {
      const selectedLocks = availableLocks.filter(
        (lock) =>
          lock.id &&
          selectedSet.has(lock.id) &&
          !!lock.externalId &&
          !!lock.siteId,
      );

      const apiClient = apiClientFactory();
      for (const lock of selectedLocks) {
        await apiClient.addLockToAccessControlGroup(groupId, {
          externalId: lock.externalId as string,
          siteId: lock.siteId as string,
          credentialType: KeyRequestCredentialType.MobileKey,
        });
      }

      onAdded();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Unable to add locks.");
    } finally {
      setAdding(false);
      setShowCloudWarning(false);
    }
  };

  const queueSummaryText = React.useMemo(() => {
    const lockNames = availableLocks
      .filter((lock) => lock.id && selectedSet.has(lock.id))
      .map((lock) => lock.name)
      .filter(Boolean)
      .slice(0, 10)
      .join(", ");

    return lockNames;
  }, [availableLocks, selectedSet]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !adding) {
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          showCloseButton={false}
          className="sm:max-w-2xl"
          onEscapeKeyDown={(event) => {
            if (adding) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (adding) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Add Locks to Group</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            {error ? <Alert variant="destructive">{error}</Alert> : null}

            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="grid flex-1 gap-2">
                <Label htmlFor="add-lock-to-group-search">Search locks</Label>
                <Input
                  id="add-lock-to-group-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="grid w-full gap-2 sm:w-[220px]">
                <Label htmlFor="add-lock-to-group-site-filter">Site</Label>
                <Select
                  value={selectedSiteId || ALL_SITES_VALUE}
                  disabled={!!groupSiteId}
                  onValueChange={(siteId) =>
                    setSelectedSiteId(
                      siteId === ALL_SITES_VALUE ? "" : siteId,
                    )
                  }
                >
                  <SelectTrigger
                    id="add-lock-to-group-site-filter"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SITES_VALUE}>All Sites</SelectItem>
                    {availableSites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="add-lock-to-group-select-all"
                checked={
                  allVisibleSelected
                    ? true
                    : someVisibleSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={toggleSelectAllVisible}
              />
              <Label htmlFor="add-lock-to-group-select-all">
                Select all ({visibleIds.length} results)
              </Label>
            </div>

            {isLoading ? (
              <div className="p-4">
                <Spinner className="size-5" />
              </div>
            ) : (
              <ul className="max-h-[380px] overflow-y-auto border">
                {filteredLocks.map((lock) => {
                  if (!lock.id) {
                    return null;
                  }

                  const isSelected = selectedSet.has(lock.id);
                  const checkboxId = `add-lock-to-group-lock-${lock.id}`;

                  return (
                    <li
                      key={lock.id}
                      className="flex items-center gap-3 border-b p-3 last:border-b-0"
                    >
                      <Checkbox
                        id={checkboxId}
                        checked={isSelected}
                        onCheckedChange={() => toggleRowSelection(lock.id as string)}
                      />
                      <label
                        className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1"
                        htmlFor={checkboxId}
                      >
                        <span className="truncate font-medium">
                          {lock.name || lock.id}
                        </span>
                        <span className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                          <Badge variant="outline">
                            {formatDoorType(lock.doorType)}
                          </Badge>
                          <span>{lock.site?.name || lock.siteId}</span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {selectedLockIds.length > 0 ? (
              <div className="text-sm">
                Queued: {queueSummaryText || `${selectedLockIds.length} locks selected`}
                <Button
                  className="ml-1"
                  disabled={adding}
                  onClick={() => setSelectedLockIds([])}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Clear all
                </Button>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button disabled={adding} onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!selectedLockIds.length) {
                  return;
                }

                if (cloudManagedSystemType) {
                  setShowCloudWarning(true);
                  return;
                }

                runAdd();
              }}
              disabled={!selectedLockIds.length || adding}
              type="button"
            >
              {adding ? "Adding..." : `Add ${selectedLockIds.length} Locks`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cloudManagedSystemType ? (
        <CloudManagedWarningDialog
          open={showCloudWarning}
          busy={adding}
          systemType={cloudManagedSystemType}
          onCancel={() => setShowCloudWarning(false)}
          onConfirm={runAdd}
        />
      ) : null}
    </>
  );
}

function formatDoorType(doorType?: AccessControlDoorType) {
  return doorType || "Unknown";
}
